// Which save of the household's PQ this page is working from.
//
// Salesforce keeps a word-for-word copy of every PQ save, and the function names
// each one by a SHA-256 fingerprint of it (pq-function/src/pqVersion.js). The
// page sends the versions it could be working from with every save, and the
// function refuses the save if Salesforce holds anything else, because then
// another page or device saved since this one last looked, and this page's copy
// would erase that work. That is what happened to Shelley Webster's PQ on
// 2026-09-21, when a page still holding the blank form saved over the advisor's
// entries.
//
// A page's versions are the one Salesforce last confirmed to it, plus any save
// it sent without hearing back (a close-time flush, a request that timed out),
// because Salesforce may well be holding that one.

const MAX_UNCONFIRMED = 5;

// The same fingerprint the function computes over the body it forwards. Null
// where the browser has no Web Crypto (only insecure http origins), in which
// case the page relies on the version the function hands back.
export async function versionOfBody(body) {
  try {
    const subtle = globalThis.crypto && globalThis.crypto.subtle;
    if (!subtle) return null;
    const bytes = await subtle.digest('SHA-256', new TextEncoder().encode(body));
    return [...new Uint8Array(bytes)].map(b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

export function emptyVersions(confirmed = '') {
  return { confirmed: confirmed || '', unconfirmed: [] };
}

// Salesforce confirmed this save, so it is the only version the page works from.
export function confirmVersion(state, version) {
  return version ? emptyVersions(version) : state;
}

// A save went out and we do not know whether it landed.
export function addUnconfirmed(state, version) {
  if (!version || version === state.confirmed || state.unconfirmed.includes(version)) return state;
  return { ...state, unconfirmed: [...state.unconfirmed, version].slice(-MAX_UNCONFIRMED) };
}

export function baseVersions(state) {
  return [state.confirmed, ...state.unconfirmed].filter(Boolean);
}

// No versions means a page that loaded before the guard shipped; it saves
// without one and the function lets it through as before.
export function saveUrl(state) {
  const bases = baseVersions(state);
  const path = '/api/FinancialProfileService';
  return bases.length ? `${path}?baseVersion=${encodeURIComponent(bases.join(','))}` : path;
}
